import { all, any, pathOr } from 'ramda';
import { createSelector } from 'reselect';

import {
  getQueryDataFromQuoteNumber,
  ComponentData,
  QUOTE_STATUS,
} from '@frontend/api';

import { PERMISSIONS, QUOTE_ITEM_STATUS, SUPPLIER_ROLES } from 'constants/all';

import { getProcesses } from 'modules/processes/data/reducer';

import { RootState } from 'store/configureStore';
import { getEntity, denormalize } from 'store/entities/selectors';
import { getOrder } from 'store/orders/selectors';
import { selectors as sessionSelectors } from 'store/session';

import { createNumericComparator } from 'utils/helpers';
import { percentage } from 'utils/numberUtils';

import * as schema from './schema';

const getScope = (state) => state.quotes;

export const getQuoteItemDenormalized = (state, quoteItemId) =>
  denormalize(state, quoteItemId, schema.quoteItem);

export const getComponentById = (
  state: RootState,
  componentId: number,
): ComponentData | undefined =>
  denormalize(state, componentId, schema.quoteItemComponent);

export const getComponentsById = (
  state: RootState,
  componentIds: number[],
): ComponentData[] =>
  componentIds.map((componentId) => getComponentById(state, componentId));

export const getBriefComponentById = (state, briefComponentId) =>
  denormalize(state, briefComponentId, schema.quoteItemBriefComponent);

export const getQuoteId = (state) => getScope(state).quoteId;

export const getIsNewQuote = (state) => getQuoteId(state) === null;

export const getIsExportingQuoteToExcel = (state) =>
  getScope(state).isExportingQuoteToExcel;

export const getExcelExportTasks = (state) => getScope(state).excelExportTasks;

export const getError = (state) => getScope(state).error;

export const getSendQuoteState = (state) => getScope(state).sendQuote;

export const getIsQuoteInCreateMode = () => {
  return window.location.pathname.includes('/create');
};

/**
 * Function that gets the quote header data from react query cache and listens
 * to the quoteHeaderLastUpdated variable in the store which is kept up to date
 * as that cache is invalidated.
 */
export const getQuote = createSelector(
  [
    (state) => state.quotes.quoteNumberWithRevision,
    (state) => state.quotes.quoteId,
    (state) => state.quotes.quoteHeaderLastUpdated,
  ],
  (quoteNumberWithRevision) => {
    return getQueryDataFromQuoteNumber(quoteNumberWithRevision);
  },
);

export const getIsQuoteHeaderLoading = (state) => {
  // if we are in create mode, we can assume the quote header is not loading
  if (getIsQuoteInCreateMode()) return false;
  // otherwise we want to check if the stored quote id is equal to the query
  // cache quote id.
  const storeId = getScope(state).quoteId;
  if (!storeId) return true;

  return (getQuote(state)?.id ?? null) !== getScope(state).quoteId;
};

export const getQuoteNumberWithRevision = (state) =>
  getScope(state)?.quoteNumberWithRevision;

export const getQuoteNumber = (state) => getQuote(state)?.number;

export const getQuoteRevisionNumber = (state) =>
  getQuote(state)?.revisionNumber;

export const getQuoteNumberRevisionNumberOrderPageCompatible = (state) => {
  let { number, revisionNumber } = getQuote(state) ?? {};
  if (number == null) {
    //we might be in order and not the BAQ page
    const order = getOrder(state) ?? {};
    number = order.quoteNumber;
    revisionNumber = order.quoteRevisionNumber;
  }
  return `${number}${revisionNumber ? `-${revisionNumber}` : ''}`;
};

export const getToken = (state) => getQuote(state)?.digiQuoteKey;

export const getParentQuote = (state) => getQuote(state)?.parentQuote;

export const getParentSupplierOrder = (state) =>
  getQuote(state)?.parentSupplierOrder;

export const getQuoteItemIds = createSelector(
  [(state) => state.entities.quoteItems, (state) => getScope(state).quoteId],
  (quoteItems, quoteId) => {
    const quoteItemIds = [];
    for (const [quoteItemId, quoteItem] of Object.entries(quoteItems)) {
      if (quoteItem.quoteId && quoteItem.quoteId === quoteId) {
        quoteItemIds.push(quoteItemId);
      }
    }
    return quoteItemIds;
  },
);

export const getQuoteItemCount = (state) => getQuoteItemIds(state).length;

export const getQuoteItems = createSelector(
  [(state) => state.entities.quoteItems, (state) => getScope(state).quoteId],
  (quoteItems, quoteId) => {
    const quoteItemList = [];
    for (const quoteItem of Object.values(quoteItems)) {
      if (quoteItem.quoteId && quoteItem.quoteId === quoteId) {
        quoteItemList.push(quoteItem);
      }
    }
    return quoteItemList.sort(createNumericComparator('position'));
  },
);

export const getDenormalizedQuoteItems = (state) => {
  return (
    denormalize(state, getQuoteItemIds(state), schema.quoteItems)
      ?.filter(Boolean)
      .sort(createNumericComparator('position')) ?? []
  );
};

export const getQuoteItemById = (state, quoteItemId) =>
  getEntity(state, 'quoteItems', quoteItemId);

export const getQuoteItemByIdDenormalized = (state, quoteItemId) =>
  denormalize(state, quoteItemId, schema.quoteItem);

export const getQuantity = (state, qtyId) =>
  getEntity(state, 'quoteItemQuantities', qtyId);

export const getQuantityDenormalized = (state, quantityId) =>
  denormalize(state, quantityId, schema.quoteItemQuantity);

export const getIsAttaching = (state, componentId) =>
  getScope(state).isAttachingByComponentId[componentId];

export const getEstimator = (state) => getQuote(state)?.estimator;

export const getSalesperson = (state) => getQuote(state)?.salesperson;

export const getSupplierFacility = (state) => getQuote(state)?.supplierFacility;

export const getTaxRate = (state) => parseFloat(getQuote(state)?.taxRate) || 0;

export const getSubtotalPrice = (state) => {
  return getDenormalizedQuoteItems(state)
    .filter((quoteItem) => quoteItem.totalPrice)
    .reduce((acc, quoteItem) => acc + parseFloat(quoteItem.totalPrice), 0);
};

export const getTaxCost = (state) =>
  percentage(getSubtotalPrice(state), getTaxRate(state));

export const getTotalPrice = (state) =>
  getSubtotalPrice(state) + getTaxCost(state);

export const getCustomerFacingNotes = (state) => getQuote(state)?.quoteNotes;

export const getQuotePriority = (state) => getQuote(state)?.priority;

export const getPrivateNotes = (state) => getQuote(state)?.privateNotes;

export const getIsPrivateNotesLoading = (state) =>
  getScope(state).isPrivateNotesLoading;

export const getIsCancelQuoteConfirmationModalOpen = (state) =>
  getScope(state).isCancelQuoteConfirmationModalOpen;

export const getQuoteSettingsById = (state) => {
  const quoteState = getScope(state);
  const processes = getProcesses(state) ?? [];
  const presets = quoteState.quoteSettingsById[quoteState.quoteId] || {};
  const activeProcess = processes.find(
    (process) => presets.process == process.id,
  );
  if (!activeProcess) {
    return {};
  }

  // need to invalidate material default selection in case the process has been
  // switched to have limited materials
  if (!activeProcess.useGlobalMaterials) {
    if (presets.material) {
      const materialIds = activeProcess.processMaterials.map(
        (mat) => mat.supplierMaterial.material.id,
      );
      if (!materialIds.includes(presets.material)) {
        presets.material = null;
      }
    }
  }

  const activeOpDefIds = activeProcess.finishOpDefIds || [];
  presets.finishOpDefIds = (presets.finishOpDefIds || []).filter((opDefId) =>
    activeOpDefIds.includes(parseInt(opDefId)),
  );
  return presets;
};

export const getQuoteStatus = (state) => getQuote(state)?.status;

export const getGroupMemberHasQuoteEditPermissions = (state) => {
  if (sessionSelectors.getIsSuperuser(state)) {
    return true;
  }

  const groupMemberSupplierRoles =
    sessionSelectors.getGroupMemberSupplierRoles(state);
  return (
    groupMemberSupplierRoles?.[SUPPLIER_ROLES.QUOTES] === PERMISSIONS.DRAFT ||
    groupMemberSupplierRoles?.[SUPPLIER_ROLES.QUOTES] === PERMISSIONS.FINALIZE
  );
};

export const getIsQuoteDraft = (state) =>
  getQuoteStatus(state) === QUOTE_STATUS.DRAFT;

export const getIsQuoteReadOnly = (state) =>
  !getIsQuoteInCreateMode() &&
  (!getGroupMemberHasQuoteEditPermissions(state) || //group member cannot draft or finalize, so quote is read only
    !getIsQuoteDraft(state));

export const getIsQuoteOutstanding = (state) =>
  getQuoteStatus(state) === QUOTE_STATUS.OUTSTANDING;

export const getIsQuoteAccepted = (state) =>
  getQuoteStatus(state) === QUOTE_STATUS.ACCEPTED;

export const getIsQuoteDeletable = (state) =>
  Boolean(getIsQuoteDraft(state) && getQuoteId(state));

export const getIsPastExpiration = (state) => getQuote(state)?.expired;

export const getExpiredDate = (state) => getQuote(state)?.expiredDate;

export const getHasRevisions = (state) => getQuote(state)?.hasRevisions;

export const getCreateRevisionFetching = (state) =>
  getScope(state)?.createRevisionFetching ?? false;

export const getCancelQuoteFetching = (state) =>
  !!(getScope(state) || {}).cancelQuoteFetching;

export const getCopyQuoteFetching = (state) =>
  !!(getScope(state) || {}).copyQuoteFetching;

export const getDeleteQuoteFetching = (state) =>
  !!(getScope(state) || {}).deleteQuoteFetching;

export const getIsExportControlledQuote = (state) =>
  !!(getDenormalizedQuoteItems(state) || []).some(
    (item) => item.exportControlled,
  ) || getQuote(state)?.exportControlled;

export const getAllExpediteOptionsBuffer = (state) =>
  (getScope(state) || {}).allExpediteOptionsBuffer;

export const getExpediteOptionsRowBuffer = (state) =>
  (getScope(state) || {}).expediteOptionsRowBuffer;

export const getIsApplyQuoteExpediteOptionsFetching = (state) =>
  (getScope(state) || {}).isApplyQuoteExpediteOptionsFetching;

export const getIsClearQuoteExpediteOptionsFetching = (state) =>
  (getScope(state) || {}).isClearQuoteExpediteOptionsFetching;

export const getRequestForQuoteInfo = (state) =>
  getQuote(state)?.requestForQuote;

export const getEmailRfq = (state) => getQuote(state)?.emailRfq;

export const getRfqNumber = (state) =>
  pathOr(null, ['rfqNumber'], getQuote(state));

export const getMarketingSource = (state) => getQuote(state)?.marketingSource;

export const getAccount = (state) =>
  pathOr(null, ['contact', 'account'], getQuote(state));

export const getContact = (state) => getQuote(state)?.contact;

export const getHasContactConflict = (state) =>
  pathOr(false, ['contactInfoConflict'], getRequestForQuoteInfo(state));

export const getContactConflictState = (state) =>
  getScope(state).contactConflict;

export const getIsUpdatingContact = (state) =>
  getScope(state).isUpdatingContact;

export const getIsUpdatingEstimator = (state) =>
  getScope(state).isUpdatingEstimator;

export const getIsUpdatingSalesperson = (state) =>
  getScope(state).isUpdatingSalesperson;

export const getIsUpdatingSupplierFacility = (state) =>
  getScope(state).isUpdatingSupplierFacility;

export const getIsUpdatingQuotePriority = (state) =>
  getScope(state).isUpdatingQuotePriority;

export const getDownloadNestPDFState = (state) =>
  getScope(state).downloadNestPDF;

export const getTaxUpdateState = (state) => getScope(state).taxUpdate;

export const getQuoteLeadTimeDisplayUnit = (state) =>
  getQuote(state)?.leadTimeDisplayUnit;

export const getBAQLoadingStatus = (state) => getScope(state).isBaqInitializing;

export const getQuoteOutstandingWorkStatus = (state) => {
  const quoteItems = getDenormalizedQuoteItems(state);

  if (
    all((qi) => qi.workflowStatus === QUOTE_ITEM_STATUS.NOT_STARTED)(quoteItems)
  ) {
    return QUOTE_ITEM_STATUS.NOT_STARTED;
  } else if (
    all(
      (qi) =>
        qi.workflowStatus === QUOTE_ITEM_STATUS.COMPLETED ||
        qi.workflowStatus === QUOTE_ITEM_STATUS.NO_QUOTE,
    )(quoteItems)
  ) {
    return QUOTE_ITEM_STATUS.COMPLETED;
  } else if (
    any((qi) => qi.workflowStatus === QUOTE_ITEM_STATUS.IN_PROGRESS)(quoteItems)
  ) {
    return QUOTE_ITEM_STATUS.IN_PROGRESS;
  } else {
    return QUOTE_ITEM_STATUS.IN_PROGRESS;
  }
};

export const getOpenTaskCount = (state) => getScope(state).openTaskCount;

export const getNumOutstandingWorkflowSteps = (state) => {
  const quoteItems = getDenormalizedQuoteItems(state);
  return quoteItems.filter(
    (qi) =>
      qi.workflowStatus !== QUOTE_ITEM_STATUS.COMPLETED &&
      qi.workflowStatus !== QUOTE_ITEM_STATUS.NO_QUOTE,
  ).length;
};

export const getConvertingToQuoteItemQuoteFileUuids = (state) =>
  getScope(state).convertingToQuoteItemQuoteFileUuids;

export const getIsMarkingQuoteAsLost = (state) =>
  getScope(state).isMarkingQuoteAsLost;

export const getIsMarkingQuoteAsSent = (state) =>
  getScope(state).isMarkingQuoteAsSent;
